import React, { useContext, useMemo, useState, useEffect } from "react";
import {
  AppBar, Toolbar, Box, Button, IconButton, Typography,
  Avatar, Menu, MenuItem, Paper, useMediaQuery, alpha, 
  ButtonBase
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  QrCode2 as QrIcon, Person as PersonIcon, Games as RoomIcon,
  ReceiptLong as OrdersIcon, Home as HomeIcon, DarkMode as DarkIcon,
  LightMode as LightIcon, Login as LoginIcon, Logout as LogoutIcon,
  Language as LangIcon
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColorModeContext } from "../App";
import useBaseImageUrl from "../hooks/useBaseImageUrl";
import logoImg from "../assets/Logo.png";
import UserQRCode from "./UserQRCode";

// الثوابت
const RED = "#D10000";
const RED_DARK = "#FF3B3B";
const MOBILE_NAV_HEIGHT = 70;

export default function NewHeader() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);
  const baseUrl = useBaseImageUrl();

  // حالة المستخدم والـ QR
  const [qrOpen, setQrOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) { return null; }
  }, [qrOpen]);

  const brand = theme.palette.mode === "dark" ? RED_DARK : RED;
  const isRtl = i18n.language === "ar";

  useEffect(() => {
    document.dir = isRtl ? "rtl" : "ltr";
  }, [isRtl]);

  const toggleLanguage = () => i18n.changeLanguage(isRtl ? "en" : "ar");
  const handleLogout = () => { localStorage.clear(); navigate("/login"); };
  const isActive = (path) => pathname === path;

  return (
    <>
      {/* 1. Desktop Header */}
      {!isMobile && (
        <AppBar position="fixed" elevation={0} sx={{ 
          bgcolor: alpha(theme.palette.background.paper, 0.8), 
          backdropFilter: "blur(15px)", borderBottom: `1px solid ${theme.palette.divider}` 
        }}>
          <Toolbar sx={{ height: 80, px: { lg: 10, md: 4 }, justifyContent: "space-between" }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <img src={logoImg} alt="RED 90 Logo" style={{ height: '40px', objectFit: 'contain' }} />
            </Link>

            <Box sx={{ display: "flex", gap: 1, p: 0.6, borderRadius: 4, bgcolor: alpha(theme.palette.divider, 0.05) }}>
              {[{ label: t("nav.home"), path: "/home" }, { label: t("nav.myBookings"), path: "/myBookings" },{label: t("nav.orders"), path: "/MyOrders" },].map((item) => (
                <Button key={item.path} component={Link} to={item.path} sx={{
                  px: 3, borderRadius: 3, fontWeight: 800, textTransform: "none",
                  color: isActive(item.path) ? "#fff" : "text.primary",
                  bgcolor: isActive(item.path) ? brand : "transparent",
                  "&:hover": { bgcolor: isActive(item.path) ? brand : alpha(brand, 0.1) }
                }}>
                  {item.label}
                </Button>
              ))}
              <Button onClick={() => setQrOpen(true)} sx={{ px: 3, borderRadius: 3, fontWeight: 800, textTransform: "none", color: "text.primary" }}>
                {t("nav.qr")}
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton onClick={toggleLanguage} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                <LangIcon fontSize="small" />
                <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 900 }}>{isRtl ? "EN" : "AR"}</Typography>
              </IconButton>
              <IconButton onClick={colorMode.toggleColorMode} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                {theme.palette.mode === "dark" ? <LightIcon fontSize="small" /> : <DarkIcon fontSize="small" />}
              </IconButton>
             {user ? (
  <ButtonBase
    onClick={(e) => setAnchorEl(e.currentTarget)}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      ml: 1,
      px: 1.5,
      py: 0.5,
      borderRadius: 2,
      transition: "0.3s",
      "&:hover": {
        bgcolor: alpha(brand, 0.08),
      },
    }}
  >
    <Box sx={{ textAlign: isRtl ? "left" : "right", display: { xs: "none", lg: "block" } }}>
      <Typography variant="subtitle2" fontWeight={900} sx={{ lineHeight: 1.2, color: "text.primary" }}>
        {user.name}
      </Typography>
    </Box>

    <Box
      sx={{
        p: 0.3,
        border: `2px solid ${brand}`,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Avatar 
        src={user?.image && user.image !== "null" && user.image !== "" ? `${baseUrl}${user.image}` : undefined} 
        sx={{ width: 35, height: 35, fontSize: 16, fontWeight: 900 }}
      >
        {user.name?.[0]}
      </Avatar>
    </Box>
  </ButtonBase>
) : (
  <Button 
    component={Link} 
    to="/login" 
    variant="contained" 
    sx={{ bgcolor: brand, borderRadius: 3, fontWeight: 900 }}
  >
    {t("nav.login")}
  </Button>
)}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* 2. Mobile Bottom Nav */}
      {isMobile && (
        <Paper elevation={20} sx={{
          position: "fixed", bottom: 0, left: 0, right: 0, height: MOBILE_NAV_HEIGHT,
          display: "grid", gridTemplateColumns: "repeat(5, 1fr)", zIndex: theme.zIndex.appBar,
          bgcolor: alpha(theme.palette.background.paper, 0.98), backdropFilter: "blur(10px)", borderTop: `1px solid ${theme.palette.divider}`,borderRadius: 0,
        }}>
          {[
            { key: "home", label: t("nav.home"), path: "/home", icon: <HomeIcon /> },
            { key: "myBookings", label: t("nav.myBookings"), path: "/MyBookings", icon: <RoomIcon /> },
            { key: "qr", label: t("nav.qr"), path: "qr", icon: <QrIcon /> },
            { key: "MyOrders", label: t("nav.orders"), path: "/MyOrders", icon: <OrdersIcon /> },
            { key: "profile", label: t("nav.profile"), path: "/profile", icon: <PersonIcon /> },
          ].map((item) => {
            const isQrBtn = item.key === "qr";
            return (
              <Box key={item.key} onClick={() => isQrBtn ? setQrOpen(true) : navigate(item.path)} sx={{ 
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" 
              }}>
                {isQrBtn ? (
                  <Box sx={{ 
                    width: 55, height: 55, borderRadius: "50%", bgcolor: theme.palette.mode === 'dark' ? '#fff' : '#000',
                    color: theme.palette.mode === 'dark' ? '#000' : '#fff', mt: -4, display: "grid", placeItems: "center",
                    border: `4px solid ${theme.palette.background.default}`, boxShadow: `0 5px 15px ${alpha(brand, 0.3)}`
                  }}>
                    <QrIcon />
                  </Box>
                ) : (
                  <Box sx={{ color: isActive(item.path) ? brand : "text.secondary", textAlign: "center" }}>
                    {item.icon}
                    <Typography sx={{ fontSize: 10, fontWeight: 800 }}>{item.label}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Unified UserQRCode Component */}
      <UserQRCode open={qrOpen} onClose={() => setQrOpen(false)} user={user} />

      {/* User Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} disableRestoreFocus PaperProps={{ sx: { borderRadius: 3, mt: 1, p: 1 } }}>
        <MenuItem onClick={() => navigate("/profile")}><PersonIcon sx={{ mr: 1, ml: 1 }} /> {t("nav.profile")}</MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}><LogoutIcon sx={{ mr: 1, ml: 1 }} /> {t("nav.logout")}</MenuItem>
      </Menu>

      {!isMobile && <Toolbar sx={{ height: 80 }} />}
    </>
  );
}
